import type { EcoleConfig, EcoleOverview } from "@shared/ecole.types";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, HttpError, messageDeSecours } from "@/lib/http";

/**
 * L'espace école tient dans un appel, comme celui de l'entreprise.
 *
 * La seule chose en plus est le CHOIX de l'école courante : 11 référents ont
 * plusieurs accès (les groupes IGENSIA, Groupe Alternance, ACE), et leur écran
 * n'écrit qu'une école à la fois.
 */

type State =
	| { status: "loading" }
	| { status: "anonymous" }
	| { status: "failed" }
	| { status: "ready"; overview: EcoleOverview };

/**
 * Un 401 dit que la session a fini ses trente jours, pas que quelque chose est
 * cassé. Les deux appels ont besoin de la distinction : la lecture pour rendre la
 * porte à code plutôt qu'un écran d'erreur, l'écriture pour ne pas laisser le
 * référent recommencer sa saisie dans le vide.
 */
function sessionFinie(error: unknown): boolean {
	return error instanceof HttpError && error.status === 401;
}

/**
 * Le motif d'un enregistrement raté, ET l'école sur laquelle il a raté.
 *
 * ⚠️ Il n'était rattaché à rien, alors que tout ce qu'il décrit l'est : le PATCH
 * part sur `/api/ecole/marque/:brandId`. Un référent de groupe qui échouait sur
 * IGENSIA Paris puis basculait sur Lyon voyait le bandeau de Paris au-dessus de
 * Lyon, sans aucun moyen de le faire disparaître autrement qu'en réussissant un
 * enregistrement sur Lyon, ce qui effaçait l'alerte réelle de Paris.
 */
type EchecEnregistrement = { brandId: string; cles: string[]; message: string };

/**
 * Les noms d'écran des champs de configuration.
 *
 * Le serveur rend des CLÉS (`contactEmail`), et une clé affichée telle quelle ne dit
 * rien à un référent. Une clé absente de ce dictionnaire ne s'affiche pas : mieux
 * vaut le message générique qu'un mot de code au milieu d'une phrase.
 */
const LIBELLES: Record<string, string> = {
	navBase: "la couleur de votre espace",
	baseline: "l'accroche",
	pageTitle: "le titre de la page",
	pageIntro: "le texte d'introduction",
	confirmationMessage: "le message de confirmation",
	contactName: "le nom de l'interlocuteur",
	contactEmail: "l'email de l'interlocuteur",
	contactPhone: "le téléphone de l'interlocuteur",
	formFields: "les champs du formulaire",
};

/** Le refus champ par champ du serveur : `{ error: "INVALID_BODY", champs: [...] }`. */
function champsRefuses(error: unknown): string[] {
	if (!(error instanceof HttpError) || error.failure.kind !== "http") return [];
	const champs = (error.failure.body as { champs?: unknown } | null)?.champs;
	if (!Array.isArray(champs)) return [];
	return champs.filter((cle): cle is string => typeof cle === "string" && cle in LIBELLES);
}

export function useEcole() {
	const [state, setState] = useState<State>({ status: "loading" });
	const [brandId, setBrandId] = useState<string | null>(null);
	const [echec, setEchec] = useState<EchecEnregistrement | null>(null);

	const load = useCallback(async () => {
		try {
			const overview = await fetchJson<EcoleOverview>("/api/ecole");
			setState({ status: "ready", overview });
			// L'école courante se garde si elle est toujours dans la liste : un
			// rechargement après enregistrement ne doit pas ramener le référent sur sa
			// première école alors qu'il configurait la troisième.
			setBrandId((current) =>
				current && overview.schools.some((school) => school.brandId === current)
					? current
					: (overview.schools[0]?.brandId ?? null),
			);
		} catch (error) {
			setState({ status: sessionFinie(error) ? "anonymous" : "failed" });
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	/**
	 * Le serveur d'abord, l'écran ensuite — comme la fiche entreprise.
	 *
	 * Ici c'est encore plus net : ce qui s'écrit devient la page PUBLIQUE de
	 * l'école. Afficher « Enregistré » avant l'accord du serveur ferait croire à un
	 * texte en ligne qui n'y est pas.
	 */
	const saveConfig = useCallback(
		async (patch: EcoleConfig): Promise<boolean> => {
			if (!brandId) return false;
			try {
				await fetchJson(`/api/ecole/marque/${brandId}`, { method: "PATCH", body: patch });
			} catch (error) {
				if (sessionFinie(error)) {
					// La porte à code repasse devant, et le motif n'a plus rien à faire
					// derrière elle.
					setEchec(null);
					setState({ status: "anonymous" });
					return false;
				}
				// Le champ ne sait dire que « Non enregistré », et il l'efface au bout de
				// six secondes : le référent ne saurait ni pourquoi, ni s'il faut
				// réessayer maintenant ou attendre. Le motif remonte donc à l'écran.
				//
				// ⚠️ Un 400 se dit autrement : « réessayez dans un instant » est faux, rien
				// ne marchera tant que la saisie n'est pas corrigée. Le serveur nomme les
				// champs fautifs, et cette information était jetée — le référent qui tapait
				// « marie.durand@ » relançait indéfiniment sans savoir laquelle de ses huit
				// valeurs était refusée.
				const champs = champsRefuses(error).map((cle) => LIBELLES[cle]);
				setEchec({
					brandId,
					cles: Object.keys(patch),
					message:
						champs.length > 0
							? `${champs.join(", ")} : cette valeur n'est pas acceptée. Corrigez-la, puis réessayez.`
							: messageDeSecours(error),
				});
				return false;
			}

			// ⚠️ On n'efface QUE si ce sont les mêmes champs qui viennent de passer. Un
			// succès quelconque effaçait l'alerte : le référent perdait son titre de page
			// sur un 503, corrigeait le nom de l'interlocuteur vingt secondes plus tard,
			// et le bandeau disparaissait alors que le titre, lui, n'était toujours pas
			// en ligne. Il quittait l'écran en pensant que tout était enregistré.
			const cles = Object.keys(patch);
			setEchec((precedent) =>
				precedent && precedent.cles.every((cle) => cles.includes(cle)) ? null : precedent,
			);
			setState((current) => {
				if (current.status !== "ready") return current;
				return {
					status: "ready",
					overview: {
						...current.overview,
						schools: current.overview.schools.map((school) =>
							school.brandId === brandId ? { ...school, ...patch } : school,
						),
					},
				};
			});
			return true;
		},
		[brandId],
	);

	const school =
		state.status === "ready"
			? (state.overview.schools.find((candidate) => candidate.brandId === brandId) ?? null)
			: null;

	// Le bandeau ne s'affiche que sur l'école où l'enregistrement a raté : changer
	// d'école le fait disparaître, et revenir dessus le fait revenir.
	const saveError = echec && echec.brandId === brandId ? echec.message : null;

	return { state, school, choose: setBrandId, saveConfig, saveError, reload: load };
}
